import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, finalize } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { OperatorAccess } from 'src/app/platform/nomenclatoare/operatori/operatori.interface';

@Component({
    selector: 'app-directie-selection-dialog',
    templateUrl: './directie-selection-dialog.component.html',
    styleUrls: ['./directie-selection-dialog.component.scss']
})
export class DirectieSelectionDialogComponent implements OnInit {
    private destroy$ = new Subject<void>();

    isLoading = false;

    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    successIcon: string = environment.config.customNotifications.icons.success;
    helper = helper;

    accesses: OperatorAccess[] = [];

    selectedAccessId: number | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { user: any, accesses: OperatorAccess[]; },
        private dialogRef: MatDialogRef<DirectieSelectionDialogComponent>,
        private operatoriService: OperatoriService,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        this.accesses = this.data.accesses;
        // Pre-select the currently active access if available
        if (this.accesses && this.accesses.length > 0) {
            const activeAccess = this.accesses.find(access => access.active);
            if (activeAccess) {
                this.selectedAccessId = activeAccess.id;
            } else {
                // If no active access, select the first one
                this.selectedAccessId = this.accesses[0].id;
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    hasAtLeastOneAccess(): boolean {
        return this.accesses && this.accesses.length > 0;
    }

    getDialogTitle(): string {
        if (!this.hasAtLeastOneAccess()) {
            return 'Nicio direcție și compartiment disponibile';
        }
        return 'Selectați direcția și compartimentul activ';
    }

    getDialogMessage(): string {
        if (!this.hasAtLeastOneAccess()) {
            return 'Nu aveți direcții și compartimente alocate. Contactați administratorul pentru a vă asigna direcții și compartimente.';
        }
        return 'Selectați ce direcție și compartiment doriți să fie active în mod implicit. Puteți schimba direcția și compartimentul active oricând din bara de navigare.';
    }

    onAccessSelect(accessId: number) {
        this.selectedAccessId = accessId;
    }

    onConfirm() {
        if (!this.selectedAccessId) {
            this.notificationService.warningSwal(
                'Atenție',
                'Trebuie să selectați direcția și compartimentul pentru a continua',
                this.errorIcon
            );
            return;
        }

        this.isLoading = true;

        this.operatoriService
            .activeOperatorAccess(this.selectedAccessId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (response) => {
                    const selectedAccess = this.accesses.find(a => a.id === this.selectedAccessId);

                    this.notificationService.warningSwal(
                        'Succes',
                        `Direcția și compartimentul au fost setate.`,
                        this.successIcon
                    );

                    this.dialogRef.close({ success: true, selectedFunctionId: this.selectedAccessId });

                    // Reload the page to refresh permissions and data
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    canCancel(): boolean {
        // Can cancel if user has at least one access (meaning they can work without setting active)
        // Cannot cancel if user has no accesses at all
        return this.hasAtLeastOneAccess();
    }
}
